import { TextareaAutosize } from '@mui/material';
import { cn } from 'cn';
import { useId } from 'react';

import { isFieldRequired, unique } from '@vestigas/utils';

import { ValidationError } from '@vestigas/ui/molecules';
import { InputLabel } from '~/ui/molecules/TanstackForm';
import { useFieldContext } from '~/ui/molecules/TanstackForm/form-context';

type P = {
  readonly disabled: boolean;
  readonly label: string;
  readonly minRows?: number;
  readonly placeholder: string;
};

/**
 * A textarea with a multi-line placeholder overlay that shows when the field
 * is empty. The textarea is transparent so the placeholder is visible through
 * it; the container provides the white background and border.
 *
 * Renders its own `InputLabel` (with required indicator derived from the field
 * validators) and `ValidationError`, mirroring the structure of the shared
 * `TextareaField` in `~/ui/molecules/TanstackForm`. Must be rendered inside a
 * `<form.AppField>` render prop so the field context is available.
 */
export function CardTextarea({ disabled, label, minRows = 5, placeholder }: P) {
  const field = useFieldContext<string>();
  const value = field.state.value ?? '';
  const hasErrors = Boolean(field.state.meta.errors?.length);

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const id = useId();

  return (
    <div className="relative flex flex-1 flex-col gap-1">
      <InputLabel htmlFor={id} isRequired={isRequired}>
        {label}
      </InputLabel>
      <div
        className={cn(
          'relative overflow-hidden rounded-sm border bg-white transition-colors duration-200 ease-out',
          hasErrors
            ? 'border-tiga-semantic-error'
            : 'border-tiga-border-default focus-within:border-tiga-action-lighter',
        )}
      >
        <TextareaAutosize
          className="box-border min-h-10 w-full resize-none bg-transparent px-[14px] py-2 text-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          id={id}
          maxRows={10}
          minRows={minRows}
          value={value}
          onBlur={field.handleBlur}
          onChange={(event) => field.handleChange(event.target.value)}
        />
        {!value.trim() && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 px-[14px] py-2 text-sm whitespace-pre-wrap text-tiga-text-lighter italic"
          >
            {placeholder}
          </div>
        )}
      </div>
      {hasErrors ? (
        <ValidationError>
          {unique(field.state.meta.errors.map(({ message }) => message)).join(
            ', ',
          )}
        </ValidationError>
      ) : null}
    </div>
  );
}
